/**
 * The address bar as the record of what a screen is showing.
 *
 * A screen is worth sharing - "this list, over this window, ranked this way, with these columns" - and a
 * link that drops the filters shows the recipient something else entirely. So the URL, not component
 * state, is where a view's controls live, and the base classes keep it in step; no view writes this
 * itself.
 *
 * Three rules the whole system rests on:
 *
 * - **The URL wins over stored preferences.** A shared link must show what the sender saw, not what
 *   the recipient last picked. Anything remembered locally is only consulted when the link is silent.
 * - **Defaults are omitted.** A URL carrying every control at its default value is unreadable and
 *   makes an unfiltered screen look filtered.
 * - **`replaceState`, never `push`.** Typing in a search box would otherwise leave a history entry per
 *   keystroke, and Back would walk out of the page one character at a time.
 */

/** Parameters the time picker owns; a view may not use these names for its own filters. */
export const RESERVED_PARAMS = new Set(['range', 'from', 'to']);

const listeners = new Set();

/**
 * Tells everything reading the address bar that it moved.
 *
 * Exported because the router pushes state of its own, and a view has to notice: a cell linking to
 * `?item=b` from a page already showing `?item=a` re-renders the *same* element, so nothing reconnects
 * and nothing re-reads. `pushState` fires no event, which is why this is a call rather than something to
 * listen for.
 */
export function notifyUrlChange()
{
   for (const listener of listeners)
   {
      listener();
   }
}

addEventListener('popstate', notifyUrlChange);

export function currentParams()
{
   return new URLSearchParams(location.search);
}

/**
 * Merges values into the query string. A value that is null, undefined or empty removes its
 * parameter, which is how a control returning to its default disappears from the link.
 */
export function applyParams(updates)
{
   const params = currentParams();

   for (const [key, value] of Object.entries(updates))
   {
      // A list is what repeated parameters are for - `?where=a&where=b` - which is how a filter made
      // of several parts stays readable instead of being packed into one encoded blob.
      if (Array.isArray(value))
      {
         params.delete(key);
         for (const entry of value.filter(Boolean))
         {
            params.append(key, String(entry));
         }
      }
      else if (value === null || value === undefined || value === '')
      {
         params.delete(key);
      }
      else
      {
         params.set(key, String(value));
      }
   }

   const query = params.toString();
   const next = query ? `${location.pathname}?${query}` : location.pathname;

   // Writing an identical URL would still push a history entry on some browsers, and would make the
   // "did anything change?" check below meaningless.
   if (next !== location.pathname + location.search)
   {
      history.replaceState(history.state, '', next);

      // `replaceState` fires nothing of its own, so anything showing the current URL - the share
      // button most of all - would go stale the moment a filter moved.
      notifyUrlChange();
   }
}

/** Fires whenever the address bar changes: a filter written here, or Back and Forward. */
export function onUrlChange(listener)
{
   listeners.add(listener);
   return () => listeners.delete(listener);
}

/**
 * Turns a parameter's text back into the type its default declares.
 *
 * The default is the type declaration: a number default means the value is a number, so a view reads
 * `this.limit + 1` rather than `'10' + 1`. A null default means "a string, or nothing".
 */
export function decodeValue(text, fallback)
{
   if (text === null)
   {
      return fallback;
   }

   if (typeof fallback === 'number')
   {
      const value = Number(text);
      return Number.isFinite(value) ? value : fallback;
   }

   if (typeof fallback === 'boolean')
   {
      return text === 'true' || text === '1';
   }

   return text;
}

/**
 * Reads the declared filters out of the URL.
 *
 * @param {Record<string, unknown>} filters  Property name to default value, or to
 *    `{ default, encode, decode }` for a value the URL cannot carry as a scalar. Add `multi: true`
 *    and the parameter repeats: `encode` returns a list of strings and `decode` receives one.
 */
export function readFilters(filters)
{
   const params = currentParams();
   const values = {};

   for (const [name, declared] of Object.entries(filters))
   {
      const spec = isCodec(declared) ? declared : { default: declared };

      if (spec.multi)
      {
         const texts = params.getAll(name);
         values[name] = texts.length === 0 ? spec.default : spec.decode(texts);
         continue;
      }

      const text = params.get(name);
      values[name] = text === null
         ? spec.default
         : (spec.decode ? spec.decode(text) : decodeValue(text, spec.default));
   }

   return values;
}

/** Writes the declared filters, omitting every one still at its default. */
export function writeFilters(host, filters)
{
   const updates = {};

   for (const [name, declared] of Object.entries(filters))
   {
      const spec = isCodec(declared) ? declared : { default: declared };
      const value = host[name];

      if (spec.multi)
      {
         updates[name] = value === null || value === undefined ? [] : spec.encode(value);
         continue;
      }

      const encoded = value === null || value === undefined
         ? ''
         : (spec.encode ? spec.encode(value) : String(value));

      const fallback = spec.default === null || spec.default === undefined
         ? ''
         : (spec.encode ? spec.encode(spec.default) : String(spec.default));

      updates[name] = encoded === fallback ? '' : encoded;
   }

   applyParams(updates);
}

/**
 * Whether a filter is still holding the value the URL says it is.
 *
 * Identity is enough for a scalar and is wrong for anything else: {@link readFilters} decodes a
 * **fresh array** for a `multi` filter every time it is asked, so `!==` reports a change on every read.
 * A view that reloads when a filter moved would then reload whenever *anything* moved the address bar,
 * including its own write-back - one open of a drill-down would cost the page's whole batch, and the
 * reload would throw away the memo the answer had just been put in.
 *
 * What is compared instead is what the link would carry, which is the only thing this system treats as
 * a filter value's identity anywhere else: `writeFilters` decides whether to omit a parameter the same
 * way.
 */
export function sameFilterValue(declared, left, right)
{
   if (left === right)
   {
      return true;
   }

   if (!isCodec(declared))
   {
      return false;
   }

   const written = value => (value === null || value === undefined
      ? ''
      : JSON.stringify(declared.encode ? declared.encode(value) : String(value)));

   return written(left) === written(right);
}

/**
 * Whether a declaration is a `{ default, encode, decode }` codec rather than a bare default value.
 *
 * Exported because anything that reads a declared filter has to make the same distinction, and three
 * hand-written copies of it is how one of them ends up disagreeing - `AppView` assigns the defaults in
 * its constructor and merges a route's overrides over them, and both must take `spec.default` rather
 * than the spec.
 */
export function isCodec(declared)
{
   return declared !== null && typeof declared === 'object' && 'default' in declared;
}
